import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SEOHead } from '@/components/SEOHead';

const sections = [
  { id: 'logfiles', label: 'Server-Logfiles' },
  { id: 'kundenkonto', label: 'Kundenkonto & Bestellungen' },
  { id: 'kontakt', label: 'Kontakt' },
  { id: 'zahlungsdienstleister', label: 'Zahlungsdienstleister' },
  { id: 'cookies', label: 'Cookies' },
  { id: 'analyse', label: 'Analyse & Kommunikation' },
  { id: 'betroffenenrechte', label: 'Betroffenenrechte' },
];

const scrollTo = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export default function DatenschutzPage() {
  const [active, setActive] = useState('logfiles');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <Layout>
      <SEOHead
        title="Datenschutzerklärung"
        description="Datenschutzerklärung von DeinServerHost gemäß DSGVO. Stand: 7. Mai 2024."
        canonical="/privacy"
        noindex={true}
      />
      {/* Hero */}
      <section className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
          <p className="text-xs font-mono text-primary uppercase tracking-widest mb-3">Rechtliches</p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-foreground">Datenschutzerklärung</h1>
          <p className="mt-4 text-muted-foreground">Letzte Aktualisierung: 07. Mai 2024</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="lg:grid lg:grid-cols-[240px_1fr] lg:gap-12">

          {/* TOC Sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-1">
              <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-3 px-3">Inhalt</p>
              {sections.map((s) => (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  className={cn(
                    'w-full text-left flex items-center gap-2 px-3 py-1.5 rounded text-sm transition-colors',
                    active === s.id
                      ? 'text-primary bg-primary/10 font-medium'
                      : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                  )}
                >
                  {active === s.id && <ChevronRight className="w-3 h-3 shrink-0" />}
                  <span className={active === s.id ? '' : 'ml-5'}>{s.label}</span>
                </button>
              ))}
            </div>
          </aside>

          {/* Content */}
          <article className="space-y-10 text-sm text-muted-foreground leading-relaxed" data-testid="datenschutz-content">

            <div className="bg-card border border-border rounded-xl p-5">
              <p>Soweit nachstehend keine anderen Angaben gemacht werden, ist die Bereitstellung Ihrer personenbezogenen Daten weder gesetzlich oder vertraglich vorgeschrieben, noch für einen Vertragsabschluss erforderlich. Sie sind zur Bereitstellung der Daten nicht verpflichtet. „Personenbezogene Daten" sind alle Informationen, die sich auf eine identifizierte oder identifizierbare natürliche Person beziehen.</p>
            </div>

            <DS_Section id="logfiles" title="Server-Logfiles">
              <p>Sie können unsere Webseiten besuchen, ohne Angaben zu Ihrer Person zu machen. Bei jedem Zugriff auf unsere Website werden an uns oder unseren Webhoster Nutzungsdaten durch Ihren Internet Browser übermittelt und in Protokolldaten (sog. Server-Logfiles) gespeichert. Zu diesen gespeicherten Daten gehören z.B. der Name der aufgerufenen Seite, Datum und Uhrzeit des Abrufs, die IP-Adresse, die übertragene Datenmenge und der anfragende Provider.</p>
              <p className="mt-2">Die Verarbeitung erfolgt auf Grundlage des Art. 6 Abs. 1 lit. f DSGVO aus unserem überwiegenden berechtigten Interesse an der Gewährleistung eines störungsfreien Betriebs unserer Website sowie zur Verbesserung unseres Angebotes.</p>
            </DS_Section>

            <DS_Section id="kundenkonto" title="Kundenkonto & Bestellungen">
              <Heading>Kundenkonto</Heading>
              <p>Bei der Eröffnung eines Kundenkontos erheben wir Ihre personenbezogenen Daten in dem dort angegebenen Umfang. Die Datenverarbeitung dient dem Zweck, Ihr Einkaufserlebnis zu verbessern und die Bestellabwicklung zu vereinfachen. Die Verarbeitung erfolgt auf Grundlage des Art. 6 Abs. 1 lit. a DSGVO mit Ihrer Einwilligung. Sie können Ihre Einwilligung jederzeit widerrufen, ohne dass die Rechtmäßigkeit der bisherigen Verarbeitung berührt wird.</p>

              <Heading>Erhebung und Weitergabe bei Bestellungen</Heading>
              <p>Bei der Bestellung erheben und verarbeiten wir Ihre personenbezogenen Daten nur, soweit dies zur Erfüllung und Abwicklung Ihrer Bestellung erforderlich ist. Die Bereitstellung der Daten ist für den Vertragsschluss erforderlich. Eine Weitergabe Ihrer Daten erfolgt an Versandunternehmen, Zahlungsdienstleister und IT-Dienstleister. Die Verarbeitung erfolgt auf Grundlage des Art. 6 Abs. 1 lit. b DSGVO.</p>
            </DS_Section>

            <DS_Section id="kontakt" title="Kontakt">
              <Heading>Verantwortlicher</Heading>
              <p>Verantwortlicher für die Datenverarbeitung ist: <span className="text-foreground font-medium">Christian Ralph Hennig, Kirchplatz 17, 66571 Eppelborn, Deutschland, +49 6881 5959100, info@deinserverhost.de</span></p>

              <Heading>Datenverarbeitung bei Nutzung des Meldesystems (TCO-VO / Digital Services Act)</Heading>
              <p>Sofern Sie uns Informationen zu mutmaßlich terroristischen oder rechtswidrigen Inhalten übermitteln, erheben wir Ihre personenbezogenen Daten nur in dem von Ihnen zur Verfügung gestellten Umfang. Die Verarbeitung erfolgt zur Erfüllung rechtlicher Verpflichtungen auf Grundlage des Art. 6 Abs. 1 lit. c DSGVO.</p>

              <Heading>Initiativ-Kontaktaufnahme per E-Mail</Heading>
              <p>Wenn Sie per E-Mail mit uns in Geschäftskontakt treten, erheben wir Ihre Daten (Name, E-Mail-Adresse, Nachrichtentext) nur in dem von Ihnen zur Verfügung gestellten Umfang. Ihre E-Mail-Adresse nutzen wir nur zur Bearbeitung Ihrer Anfrage. Ihre Daten werden anschließend unter Beachtung gesetzlicher Aufbewahrungsfristen gelöscht.</p>

              <Heading>Kontaktformular</Heading>
              <p>Bei der Nutzung des Kontaktformulars erheben wir Ihre personenbezogenen Daten (Name, E-Mail-Adresse, Nachrichtentext) nur in dem von Ihnen zur Verfügung gestellten Umfang. Ihre Daten werden nach Abschluss gesetzlicher Aufbewahrungsfristen gelöscht, sofern Sie der weitergehenden Nutzung nicht zugestimmt haben.</p>
            </DS_Section>

            <DS_Section id="zahlungsdienstleister" title="Zahlungsdienstleister">
              <Heading>PayPal</Heading>
              <p>Alle PayPal-Transaktionen unterliegen der PayPal-Datenschutzerklärung unter{' '}
                <a href="https://www.paypal.com/de/webapps/mpp/ua/privacy-full" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">paypal.com/de</a>.
              </p>

              <Heading>Klarna</Heading>
              <p>Um Ihnen die Zahlungsoptionen von Klarna anbieten zu können, werden personenbezogene Daten (Kontaktdaten, Bestelldaten) an Klarna übermittelt. Informationen unter{' '}
                <a href="https://www.klarna.com/de/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">klarna.com/de</a>.
              </p>

              <Heading>Stripe</Heading>
              <p>Wir verwenden den Zahlungsdienst Stripe der Stripe Payments Europe Ltd., Dublin, Irland. Die Datenverarbeitung dient dem Zweck, Ihnen die Zahlung über diesen Dienst anbieten zu können. Die Verarbeitung erfolgt auf Grundlage des Art. 6 Abs. 1 lit. b DSGVO.</p>
              <p className="mt-2">Stripe behält sich vor, ggf. eine Bonitätsauskunft einzuholen. <strong className="text-foreground">Sie haben das Recht, aus Gründen Ihrer besonderen Situation jederzeit gegen diese Verarbeitung zu widersprechen.</strong></p>
              <p className="mt-1">Datenschutzerklärung:{' '}
                <a href="https://stripe.com/de/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">stripe.com/de/privacy</a>
              </p>

              <Heading>Link (Stripe)</Heading>
              <p>Wir verwenden den Zahlungsdienst Link der Stripe Payments Europe Ltd. Zur Einbindung ist es erforderlich, dass Stripe beim Aufruf der Website Daten (IP-Adresse, Gerätetyp, Browsertyp etc.) sammelt. Die Nutzung erfolgt auf Grundlage des § 25 Abs. 1 TDDDG i.V.m. Art. 6 Abs. 1 lit. a DSGVO mit Ihrer Einwilligung.</p>
            </DS_Section>

            <DS_Section id="cookies" title="Cookies">
              <p>Unsere Website verwendet Cookies. Cookies sind kleine Textdateien, die im Internetbrowser auf dem Computersystem eines Nutzers gespeichert werden. Wir setzen diese Cookies ein, um unser Angebot nutzerfreundlicher, effektiver und sicherer zu machen.</p>
              <p className="mt-2">Cookies werden auf Ihrem Rechner gespeichert. Daher haben Sie die volle Kontrolle über die Verwendung von Cookies. Durch die Auswahl entsprechender technischer Einstellungen in Ihrem Internetbrowser können Sie die Speicherung der Cookies verhindern.</p>
              <p className="mt-3 font-medium text-foreground">Cookie-Verwaltung in den wichtigsten Browsern:</p>
              <ul className="mt-2 space-y-1 list-disc list-inside">
                <li><a href="https://support.google.com/accounts/answer/61416?hl=de" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Chrome Browser</a></li>
                <li><a href="https://support.microsoft.com/de-de/help/17442/windows-internet-explorer-delete-manage-cookies" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Internet Explorer</a></li>
                <li><a href="https://support.mozilla.org/de/kb/cookies-erlauben-und-ablehnen" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/de-de/guide/safari/manage-cookies-and-website-data-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Safari</a></li>
              </ul>

              <Heading>Technisch notwendige Cookies</Heading>
              <p>Wir setzen ausschließlich technisch notwendige Cookies ein. Die Nutzung erfolgt auf Grundlage des § 25 Abs. 2 TTDSG sowie Art. 6 Abs. 1 lit. f DSGVO. <strong className="text-foreground">Sie haben das Recht, dieser Verarbeitung jederzeit zu widersprechen.</strong></p>
            </DS_Section>

            <DS_Section id="analyse" title="Analyse & Kommunikation">
              <p>Die in diesem Abschnitt dargestellten Datenverarbeitungen erfolgen auf Grundlage des Art. 6 Abs. 1 lit. f DSGVO aus unserem berechtigten Interesse an der bedarfsgerechten Gestaltung der Website.</p>
              <p className="mt-2 font-medium text-foreground">Sie haben das Recht, aus Gründen Ihrer besonderen Situation jederzeit dieser auf Art. 6 Abs. 1 lit. f DSGVO beruhenden Verarbeitungen zu widersprechen.</p>

              <Heading>Chatra (Live-Chat)</Heading>
              <p>Wir verwenden das Live-Chat-System der Roger Wilco LLC (Arlington, VA, USA; „Chatra") für die Kommunikation zwischen Ihnen und uns. Aus diesen Daten können unter einem Pseudonym Nutzungsprofile erstellt werden. Hierzu werden Cookies eingesetzt. Ihre Daten können in die USA übermittelt werden.</p>
              <p className="mt-1">Datenschutzhinweise:{' '}
                <a href="https://chatra.io/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">chatra.io/privacy-policy</a>
              </p>

              <Heading>Google reCAPTCHA</Heading>
              <p>Wir verwenden reCAPTCHA der Google Ireland Limited (Gordon House, Barrow Street, Dublin 4, Irland) zur Unterscheidung menschlicher und maschineller Eingaben. Dabei werden die Eingabe sowie IP-Adresse und weitere Daten an Google übermittelt und in der EU verarbeitet.</p>
              <p className="mt-1">Datenschutzerklärung:{' '}
                <a href="https://www.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">google.com/privacy</a>
              </p>
            </DS_Section>

            <DS_Section id="betroffenenrechte" title="Betroffenenrechte & Speicherdauer">
              <Heading>Dauer der Speicherung</Heading>
              <p>Nach vollständiger Vertragsabwicklung werden die Daten zunächst für die Dauer der Gewährleistungsfrist, danach unter Berücksichtigung gesetzlicher Aufbewahrungsfristen gespeichert und dann nach Fristablauf gelöscht.</p>

              <Heading>Ihre Rechte (Art. 15–20 DSGVO)</Heading>
              <ul className="mt-2 space-y-1 list-disc list-inside">
                <li>Recht auf Auskunft</li>
                <li>Recht auf Berichtigung</li>
                <li>Recht auf Löschung</li>
                <li>Recht auf Einschränkung der Verarbeitung</li>
                <li>Recht auf Datenübertragbarkeit</li>
                <li>Widerspruchsrecht (Art. 21 Abs. 1 DSGVO)</li>
              </ul>
              <p className="mt-2">Kontaktieren Sie uns auf Wunsch. Die Kontaktdaten finden Sie in unserem <Link to="/imprint" className="text-primary hover:underline">Impressum</Link>.</p>

              <Heading>Beschwerderecht bei der Aufsichtsbehörde</Heading>
              <p>Sie haben gemäß Art. 77 DSGVO das Recht, sich bei der Aufsichtsbehörde zu beschweren, wenn Sie der Ansicht sind, dass die Verarbeitung Ihrer personenbezogenen Daten nicht rechtmäßig erfolgt.</p>

              <Heading>Widerspruchsrecht</Heading>
              <p>Beruhen die hier aufgeführten Datenverarbeitungen auf Grundlage unseres berechtigten Interesses nach Art. 6 Abs. 1 lit. f DSGVO, haben Sie das Recht, jederzeit diesen Verarbeitungen mit Wirkung für die Zukunft zu widersprechen.</p>
            </DS_Section>

            <div className="bg-card border border-border rounded-xl p-4 text-xs text-muted-foreground">
              Letzte Aktualisierung: 07.05.2024
            </div>

            {/* Footer Nav */}
            <div className="pt-4 border-t border-border flex flex-wrap gap-4 text-sm text-muted-foreground">
              <Link to="/imprint" className="hover:text-foreground transition-colors">Impressum</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">AGB</Link>
              <Link to="/cookie-settings" className="hover:text-foreground transition-colors">Cookie-Einstellungen</Link>
            </div>
          </article>
        </div>
      </div>
    </Layout>
  );
}

const DS_Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24">
    <h2 className="text-lg font-bold text-foreground mb-4 pb-2 border-b border-border">{title}</h2>
    <div className="space-y-3">{children}</div>
  </section>
);

const Heading = ({ children }) => (
  <h3 className="font-semibold text-foreground mt-4 mb-1">{children}</h3>
);
